import { FC } from "react";

import { Shelf } from "../../../layout";
import { Tag as BaseTag, TagType } from "../../Tag";
import { BaseColumnProps } from "../BaseColumnProps";
import { ColumnElementConstructor } from "../ColumnElementConstructor";
import { Custom } from "./Custom";

/** The `Table.Tag` column tag type provider function. */
export interface TableColumnTagTypeProvider {
  /**
   * Gets the tag type for the specified value.
   * @param value The value to get the tag type for.
   */
  (value: string | number): TagType | undefined;
}

/**
 * The `Tag` component properties.
 */
export interface TagProps extends Omit<BaseColumnProps, "align"> {
  /**
   * A function which takes the value and provides a tag type. If not specified, or if the provider returns
   * `undefined`, the tag renders the light neutral style.
   */
  readonly type?: TableColumnTagTypeProvider;
}

/**
 * Column which displays a value as one or more tags.
 */
export const Tag: FC<TagProps> = (props) => {
  const { type } = props;

  return (
    <Custom {...props}>
      {(value) => {
        if (value === null) return "--";
        if (value === undefined) return "--";

        if (Array.isArray(value)) {
          return (
            <Shelf inline spacing="mini">
              {value.map((entry, index) => {
                if (typeof entry === "string" || typeof entry === "number") {
                  return (
                    <BaseTag key={`${entry}-${index}`} type={type?.(entry)}>
                      {entry}
                    </BaseTag>
                  );
                }
              })}
            </Shelf>
          );
        } else {
          if (typeof value === "string" || typeof value === "number") {
            return <BaseTag type={type?.(value)}>{value}</BaseTag>;
          }
        }

        return "--";
      }}
    </Custom>
  );
};

(Tag as ColumnElementConstructor<TagProps>).className = "tag";
/* Tag column headers are left-aligned to match standard column alignment — tags are inline elements that read naturally left-to-right. */
(Tag as ColumnElementConstructor<TagProps>).headerAlignment = "left";
